import { useState } from 'react'
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Button,
  Stack,
  TextField,
  IconButton,
  Switch,
  Card,
  CardContent,
  MenuItem,
  Select,
  CircularProgress
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'

interface TimeRange {
  startTime: string  // stored in 24h format
  endTime: string    // stored in 24h format
  hasConflict?: boolean  // new field to track conflicts
}

interface DaySchedule {
  enabled: boolean
  timeRanges: TimeRange[]
}

interface WeeklySchedule {
  [key: string]: DaySchedule
}

interface TimeOption {
  display: string   // 12h format with AM/PM
  value: string    // 24h format
}

interface Props {
  schedule: WeeklySchedule;
  timezone: string;
  onChange: (schedule: WeeklySchedule, timezone: string) => void;
  loading?: boolean;
  disabled?: boolean;
}

const generateTimeOptions = () => {
  const options: TimeOption[] = []
  // Start from 5 AM (5) to 11 PM (23)
  for (let hour = 5; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour > 12 ? hour - 12 : hour
      const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`
      const militaryTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push({
        display: displayTime,
        value: militaryTime
      })
    }
  }
  return options
}

const timeOptions = generateTimeOptions()

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' }
]

// TimeRangeSelector Component
const TimeRangeSelector = ({
  range,
  onUpdate,
  onDelete,
  showDelete,
  hasConflict
}: {
  range: TimeRange,
  onUpdate: (field: 'startTime' | 'endTime', value: string) => void,
  onDelete?: () => void,
  showDelete?: boolean,
  hasConflict?: boolean
}) => (
  <Stack spacing={0.5}>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        border: hasConflict ? '1px solid #ffcdd2' : 'none',
        borderRadius: 1,
        p: hasConflict ? 1 : 0,
        backgroundColor: hasConflict ? '#ffebee' : 'transparent',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <TextField
        select
        label="Start"
        value={range.startTime}
        onChange={(e) => onUpdate('startTime', e.target.value)}
        size="small"
        error={hasConflict}
        sx={{
          width: { xs: 110, sm: 130 },
          '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
          '& .MuiSelect-select': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 1, sm: 1.5 }
          }
        }}
      >
        {timeOptions.map((time) => (
          <MenuItem
            key={time.value}
            value={time.value}
            disabled={time.value >= range.endTime}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {time.display}
          </MenuItem>
        ))}
      </TextField>
      <Typography
        sx={{
          color: hasConflict ? 'error.main' : 'text.secondary',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          mx: { xs: 0.5, sm: 1 }
        }}
      >
        -
      </Typography>
      <TextField
        select
        label="End"
        value={range.endTime}
        onChange={(e) => onUpdate('endTime', e.target.value)}
        size="small"
        error={hasConflict}
        sx={{
          width: { xs: 110, sm: 130 },
          '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
          '& .MuiSelect-select': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 1, sm: 1.5 }
          }
        }}
      >
        {timeOptions.map((time) => (
          <MenuItem
            key={time.value}
            value={time.value}
            disabled={time.value <= range.startTime}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {time.display}
          </MenuItem>
        ))}
      </TextField>
      {showDelete && (
        <IconButton
          onClick={onDelete}
          size="small"
          color="error"
          sx={{
            p: { xs: 0.5, sm: 1 },
            ml: { xs: 0.5, sm: 1 }
          }}
        >
          <DeleteIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
        </IconButton>
      )}
    </Stack>
    {hasConflict && (
      <Typography
        variant="caption"
        sx={{
          color: 'error.main',
          fontSize: '0.75rem',
          ml: 1,
          opacity: 0.8
        }}
      >
        This time range overlaps with another range
      </Typography>
    )}
  </Stack>
)

// DayScheduleCard Component
interface DayScheduleCardProps {
  day: { id: string, label: string }
  schedule: DaySchedule
  onToggle: () => void
  onUpdateTime: (index: number, field: 'startTime' | 'endTime', value: string) => void
  onAddRange: () => void
  onRemoveRange: (index: number) => void
  conflicts: number[]
  disabled?: boolean
}

const DayScheduleCard = ({
  day,
  schedule,
  onToggle,
  onUpdateTime,
  onAddRange,
  onRemoveRange,
  conflicts,
  disabled = false
}: DayScheduleCardProps) => (
  <Card variant="outlined" sx={{width: '100%'}} >
    <CardContent sx={{
      p: { xs: 1.5, sm: 2 },
      '&:last-child': { pb: { xs: 1.5, sm: 2 } }
    }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          pb: { xs: 1, sm: 2 },
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Switch
          checked={schedule.enabled}
          onChange={onToggle}
          size="small"
          disabled={disabled}
        />
        <Typography
          sx={{
            fontWeight: 'medium',
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
        >
          {day.label}
        </Typography>
      </Stack>

      {schedule.enabled && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {schedule.timeRanges.map((range, index) => (
            <TimeRangeSelector
              key={index}
              range={range}
              onUpdate={(field, value) => onUpdateTime(index, field, value)}
              onDelete={() => onRemoveRange(index)}
              showDelete={true}
              hasConflict={conflicts.includes(index)}
            />
          ))}
          <Button
            startIcon={<AddIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />}
            onClick={onAddRange}
            size="small"
            disabled={disabled}
            aria-label="add-time-range"
            sx={{
              alignSelf: 'flex-start',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 0.5, sm: 1 },
              color: 'black'
            }}
          >
            Add Time Range
          </Button>
        </Stack>
      )}
    </CardContent>
  </Card>
)

export default function EditAvailability({ 
  schedule, 
  timezone = 'America/New_York', 
  onChange, 
  loading = false, 
  disabled = false 
}: Props) {
  const [conflicts, setConflicts] = useState<{[key: string]: number[]}>({})

  const weekdays = [
    { id: 'MON', label: 'Monday' },
    { id: 'TUE', label: 'Tuesday' },
    { id: 'WED', label: 'Wednesday' },
    { id: 'THU', label: 'Thursday' },
    { id: 'FRI', label: 'Friday' },
    { id: 'SAT', label: 'Saturday' },
    { id: 'SUN', label: 'Sunday' },
  ]

  const checkTimeRangeOverlap = (ranges: TimeRange[]): number[] => {
    const conflicts: number[] = []

    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const range1 = ranges[i]
        const range2 = ranges[j]

        // Check if ranges overlap
        if (
          (range1.startTime <= range2.endTime && range1.endTime >= range2.startTime) ||
          (range2.startTime <= range1.endTime && range2.endTime >= range1.startTime)
        ) {
          if (!conflicts.includes(i)) conflicts.push(i)
          if (!conflicts.includes(j)) conflicts.push(j)
        }
      }
    }

    return conflicts
  }

  const addTimeRange = (dayId: string) => {
    const day = schedule?.[dayId] || { enabled: false, timeRanges: [] }

    // If there are no time ranges, start with a default time
    if (day.timeRanges.length === 0) {
      const newRanges = [{
        startTime: '13:00',
        endTime: '14:00'
      }]

      // Check for conflicts
      const dayConflicts = checkTimeRangeOverlap(newRanges)
      setConflicts(prev => ({
        ...prev,
        [dayId]: dayConflicts
      }))

      onChange({
        ...schedule,
        [dayId]: {
          ...day,
          timeRanges: newRanges
        }
      }, timezone)
      return
    }

    const lastRange = day.timeRanges[day.timeRanges.length - 1]

    // Find the next time slot after the last end time
    const lastEndTimeIndex = timeOptions.findIndex(t => t.value === lastRange.endTime)
    const newStartOption = timeOptions[lastEndTimeIndex + 5] || timeOptions[0]
    const newEndTimeIndex = lastEndTimeIndex + 10 // Two slots later (40 mins)
    const newEndOption = timeOptions[newEndTimeIndex] || timeOptions[timeOptions.length - 1]

    const newRanges = [
      ...day.timeRanges,
      {
        startTime: newStartOption.value,
        endTime: newEndOption.value
      }
    ]

    // Check for conflicts
    const dayConflicts = checkTimeRangeOverlap(newRanges)
    setConflicts(prev => ({
      ...prev,
      [dayId]: dayConflicts
    }))

    onChange({
      ...schedule,
      [dayId]: {
        ...day,
        timeRanges: newRanges
      }
    }, timezone)
  }

  const removeTimeRange = (dayId: string, index: number) => {
    const day = schedule?.[dayId] || { enabled: false, timeRanges: [] }
    const newRanges = day.timeRanges.filter((_, i) => i !== index)

    // Check for conflicts after removal
    const dayConflicts = checkTimeRangeOverlap(newRanges)
    setConflicts(prev => ({
      ...prev,
      [dayId]: dayConflicts
    }))

    onChange({
      ...schedule,
      [dayId]: {
        ...day,
        timeRanges: newRanges
      }
    }, timezone)
  }

  const updateTimeRange = (dayId: string, index: number, field: 'startTime' | 'endTime', value: string) => {
    const day = schedule?.[dayId] || { enabled: false, timeRanges: [] }
    const newRanges = [...day.timeRanges]
    newRanges[index] = { ...newRanges[index], [field]: value }

    // If updating start time, ensure end time is after start time
    if (field === 'startTime' && value >= newRanges[index].endTime) {
      const startIndex = timeOptions.findIndex(t => t.value === value)
      const newEndOption = timeOptions[startIndex + 2] || timeOptions[timeOptions.length - 1]
      newRanges[index].endTime = newEndOption.value
    }

    // Check for conflicts
    const dayConflicts = checkTimeRangeOverlap(newRanges)
    setConflicts(prev => ({
      ...prev,
      [dayId]: dayConflicts
    }))

    const newSchedule = {
      ...schedule,
      [dayId]: {
        ...day,
        timeRanges: newRanges
      }
    }
    console.log('🔄 EditAvailability: Time range updated', dayId, field, value, 'new schedule:', newSchedule);
    onChange(newSchedule, timezone)
  }

  const toggleDay = (dayId: string) => {
    const day = schedule?.[dayId] || { enabled: false, timeRanges: [] }
    const newSchedule = {
      ...schedule,
      [dayId]: {
        ...day,
        enabled: !day.enabled
      }
    }
    console.log('🔄 EditAvailability: Day toggled', dayId, 'new schedule:', newSchedule);
    onChange(newSchedule, timezone)
  }

  const handleTimezoneChange = (newTimezone: string) => {
    onChange(schedule, newTimezone)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <FormLabel sx={{ color: 'black', '&.Mui-focused': { color: 'black' } }}>
          Your Timezone
        </FormLabel>
        <Select
          value={timezone}
          onChange={(e) => handleTimezoneChange(e.target.value)}
          size="small"
          disabled={disabled}
          sx={{
            '& .MuiSelect-select': {
              color: 'black'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'black'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'black'
            }
          }}
        >
          {TIMEZONE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack spacing={2}>
        {weekdays.map(day => (
          <DayScheduleCard
            key={day.id}
            day={day}
            schedule={schedule?.[day.id] || { enabled: false, timeRanges: [] }}
            onToggle={() => toggleDay(day.id)}
            onUpdateTime={(index, field, value) => updateTimeRange(day.id, index, field, value)}
            onAddRange={() => addTimeRange(day.id)}
            onRemoveRange={(index) => removeTimeRange(day.id, index)}
            conflicts={conflicts[day.id] || []}
            disabled={disabled}
          />
        ))}
      </Stack>
    </Box>
  )
}
